<template>
  <div>
    <v-card class="mb-4">
      <v-card-text>
        <v-row class="d-flex align-center">
          <v-col
            cols="12"
            md="6"
          >
            <h2>Hi {{ userData.name }}, Welcome back!</h2>
          </v-col>
          <v-col
            cols="12"
            md="3"
            offset-md="3"
          >
            <!-- filter months -->
            <v-menu
              v-model="isMonthMenuOpen"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :append-icon="icons.mdiCalendar"
                  :value="monthRangeText"
                  readonly
                  dense
                  outlined
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="monthRange"
                type="month"
                color="primary"
                range
              >
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  block
                  @click="monthRangeChange()"
                >
                  {{ t('Set Dates') }}
                </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-row>
      <v-col
        cols="12"
        md="4"
      >
        <v-card>
          <v-card-title class="pb-1">
            <span class="text-h6">Your Policies Issued</span>
          </v-card-title>

          <v-card-text class="text-h6">
            <PoliciesIssuedBreakdown
              v-for="customerType in ['Quoted', 'Non quoted/Broker', 'Corporate', 'Niagamas loyalty', 'Staff rate', 'Niagamas merchant', '']"
              :key="customerType"
              :customer-type="customerType"
              :summary="policies"
            />
            <v-divider class="divider-width primary mt-2"></v-divider>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="4"
      >
        <v-card>
          <v-card-title class="pb-1">
            <span class="text-h6">Your Outstanding Invoices</span>
          </v-card-title>

          <v-card-text class="text-h6 text--primary pt-3">
            <template v-if="outstandingInvoices && outstandingInvoices.length === 0">
              <span>0</span>
            </template>
            <template v-else>
              <div
                v-for="(value, key) in outstandingInvoices"
                :key="key"
                class="d-flex align-center flex-grow-1 flex-wrap"
              >
                <router-link
                  class="font-weight-medium text-decoration-none"
                  :to="{ name: 'apps-invoice-preview', params: { id: value[0] } }"
                >
                  {{ value[1] }}
                </router-link>
                <v-spacer></v-spacer>
                <span class="text--primary">
                  {{ value[2] }}
                </span>
              </div>
            </template>
            <v-divider class="divider-width warning mt-2"></v-divider>
          </v-card-text>
        </v-card>
        <v-card class="mt-4">
          <v-card-title class="pb-1">
            <span class="text-h6">Your Commission</span>
          </v-card-title>

          <v-card-text class="text-h6 text--primary pt-3">
            <span>{{ commission }}</span>
            <v-divider class="divider-width info mt-2"></v-divider>
          </v-card-text>
        </v-card>
        <v-card
          v-if="userRole === 'Clerk' || userRole === 'SPD reporter'"
          class="mt-4"
        >
          <v-card-title class="pb-0">
            <div style="width: 100%; display: flex; justify-content: space-between;">
              <span class="text-h6">Your Rotations</span>
              <span class="pt-3" style="font-size: 11px">Minimum of 20 rotations per term</span>
            </div>
          </v-card-title>

          <v-card-text class="text-h6 text--primary pt-3">
            <span>{{ rotations }}</span>
            <v-divider class="divider-width purple mt-2"></v-divider>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        md="4"
      >
        <v-card
          v-if="userRole === 'Clerk' || userRole === 'SPD reporter' || userRole === 'Secretary'"
          id="leave-balance-card"
        >
          <v-card-title class="pb-1">
            <span class="text-h6">Your Leave Balance</span>
          </v-card-title>

          <v-card-text class="text-h6 text--primary">
            <v-list
              v-for="(value, key) in leaveBalance"
              :key="key"
              class="pt-1"
            >
              <v-list-item class="px-0">
                <div class="d-flex align-center flex-grow-1 flex-wrap">
                  <div class="me-2">
                    <span class="text-no-wrap">
                      {{ formatLeaveType(key) }}
                    </span>
                  </div>
                  <v-spacer></v-spacer>
                  <div class="d-flex align-center">
                    <span class="text--primary me-1">
                      {{ value }}
                    </span>
                  </div>
                </div>
              </v-list-item>
            </v-list>
            <v-divider class="divider-width accent mt-2"></v-divider>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog
      v-model="invoiceReminderDialog"
      width="520"
      persistent
    >
      <v-card>
        <v-card-title
          class="d-flex align-center mv-4"
        >
          Invoices expiring soon
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            @click="invoiceReminderDialog = false"
          >
            <v-icon size="22">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle v-if="userData.branch">
          <span>{{ userData.branch.name }}</span>
        </v-card-subtitle>

        <v-card-text>
          <div v-for="inv in expiringInvoices" :key="inv.id">
            <div class="border-solid px-2 py-4 mb-2" :class="expiryStatusVariant(dueDateDiff(inv.due_date))">
              <div class="row">
                <div class="col-4">
                  <router-link
                    class="font-weight-bold text--primary text-decoration-none"
                    :to="{ name: 'apps-invoice-preview', params: { id: inv.id } }"
                  >
                    {{ inv.invoice_number }}
                  </router-link>
                  <div>
                    <small>{{ dueDateDiff(inv.due_date) }} days left</small>
                  </div>
                </div>
                <div class="col-4 offset-4 pt-5 text-right">{{ inv.due_date }}</div>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mdiCalendar,
  mdiOpenInNew,
  mdiClose,
} from '@mdi/js'
import {
  inject,
  ref,
  onMounted,
  computed,
} from '@vue/composition-api'
import PoliciesIssuedBreakdown from '@/components/apps/summaries/PoliciesIssuedBreakdown.vue'

export default {
  components: {
    PoliciesIssuedBreakdown,
  },
  setup() {
    // Initializers
    const store = inject('store')
    const snackbarService = inject('snackbarService')
    const t = inject('t')
    const userData = JSON.parse(localStorage.getItem('userData'))
    const userRole = userData && userData.role ? userData.role : null

    // Properties
    const [dayNow, monthNow, yearNow] = new Date().toLocaleDateString('en-SG').split('/')
    const doneFetching = ref(false)
    const dateRange = ref([`${yearNow}-${monthNow}-01`, `${yearNow}-${monthNow}-28`])
    const monthRange = ref([`${yearNow}-${monthNow}`])
    const isDateMenuOpen = ref(false)
    const isMonthMenuOpen = ref(false)
    const outstandingInvoices = ref(null)
    const rotations = ref(null)
    const policies = ref({})
    const commission = ref(null)
    const leaveBalance = ref({})
    const expiringInvoices = ref([])
    const invoiceReminderDialog = ref(false)

    // Methods
    const fetchClerkAnalytics = () => {
      doneFetching.value = false
      store
        .dispatch('fetchClerkAnalytics', {
          start_month: monthRange.value[0],
          end_month: monthRange.value[1] ? monthRange.value[1] : monthRange.value[0],
        })
        .then(response => {
          const { data } = response.data
          outstandingInvoices.value = data.invoices
          policies.value = data.policies
          commission.value = data.commission
          rotations.value = data.rotations

          doneFetching.value = true
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while generating analytics report. Please refresh!')
        })
    }

    // Computed
    const monthRangeText = computed(() => {
      if (Date.parse(monthRange.value[0]) > Date.parse(monthRange.value[1])) {
        const [start, end] = monthRange.value
        monthRange.value[0] = end
        monthRange.value[1] = start
      }

      const startDateText = monthRange.value[0]
      const showEndDate = dateRange.value[0] !== dateRange.value[1] && monthRange.value[1]
      const endDateText = showEndDate ? ` - ${monthRange.value[1]}` : ''

      return `${startDateText}${endDateText}`
    })
    const monthRangeChange = () => {
      isMonthMenuOpen.value = false
      fetchClerkAnalytics()
    }
    const fetchLeaveBalance = () => {
      store
        .dispatch('eventStore/fetchLeaveBalance')
        .then(response => {
          const { data } = response.data
          leaveBalance.value = data
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching leave balance. Please refresh!')
        })
    }
    const fetchInvoiceReminder = () => {
      store
        .dispatch('eventStore/fetchInvoiceReminder', {
          branch_id: userData.temporary_position_id ? userData.temporary_position_id : (userData.branch?.id ?? null),
        })
        .then(response => {
          expiringInvoices.value = response.data.data
          if (expiringInvoices.value.length > 0) {
            invoiceReminderDialog.value = true
          }
        })
        .catch(error => {
          snackbarService.error(error.response.data.message || 'Something went wrong while fetching expiring invoices. Please refresh!')
        })
    }
    const formatLeaveType = key => key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    const dueDateDiff = date => {
      const date1 = new Date()
      const date2 = new Date(date.split('/').reverse().join('-'))

      const diffInMs = Math.abs(date2 - date1)

      const msPerDay = 1000 * 60 * 60 * 24
      const diffInDays = Math.ceil(diffInMs / msPerDay)

      return diffInDays
    }
    const expiryStatusVariant = diff => {
      let variant = ''
      if (diff === 1) {
        variant = 'border-red'
      } else if (diff < 4) {
        variant = 'border-warning'
      }

      return variant
    }

    // Mounted
    onMounted(async () => {
      fetchClerkAnalytics()
      fetchLeaveBalance()
      if (userRole === 'Clerk') fetchInvoiceReminder()
    })

    return {
      t,
      userData,
      userRole,
      dayNow,
      doneFetching,
      isDateMenuOpen,
      isMonthMenuOpen,
      dateRange,
      monthRange,
      monthRangeText,
      monthRangeChange,
      outstandingInvoices,
      rotations,
      policies,
      commission,
      formatLeaveType,
      leaveBalance,
      expiringInvoices,
      invoiceReminderDialog,
      dueDateDiff,
      expiryStatusVariant,
      icons: {
        mdiCalendar,
        mdiOpenInNew,
        mdiClose,
      },
      components: {
        PoliciesIssuedBreakdown,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
  .divider-width {
    border-width: 2px;
  }
  .border-red {
    border: 1px solid #D50000;
    border-radius: 5px;
  }
  .border-warning {
    border: 1px solid #FBC02D;
    border-radius: 5px;
  }
  #leave-balance-card {
    .v-data-table {
      .v-data-table__wrapper {
        table {
          tbody, thead {
            tr {
              td, th {
                padding: 0 !important;
                height: 50px !important;
              }
            }
          }
        }
      }
    }
  }
</style>
